import { message } from 'antd';
import { t } from 'i18next';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _set from 'lodash/set';
import _toNumber from 'lodash/toNumber';
import { ObjectSchema } from 'yup';
import { ObjectShape } from 'yup/lib/object';

import { TGlobalListPage } from './globalTranslation/TModules';

/**
 * Write your own validations for form here
 * @param {*} value
 */
export const required = value => (value ? undefined : t(`${TGlobalListPage}.Required`));
export const minValue = (min: number) => value =>
  value && value < min ? t(`${TGlobalListPage}.MinValue`, { min }) : undefined;
export const maxValue = (max: number) => value =>
  value && value > max ? t(`${TGlobalListPage}.MaxValue`, { max }) : undefined;
export const minValue0 = minValue(0);
export const minValue1 = minValue(1);

export const isInteger = value =>
  !Number.isInteger(Number(value)) ? t(`${TGlobalListPage}.MustBeInteger`) : undefined;
export const number = value =>
  value && isNaN(Number(value)) ? t(`${TGlobalListPage}.MustBeNumber`) : undefined;
export const email = value =>
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)
    ? t(`${TGlobalListPage}.InvalidEmail`)
    : undefined;
export const urlValidate = value =>
  value &&
  !/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i.test(
    value
  )
    ? t(`${TGlobalListPage}.InvalidUrl`)
    : undefined;
export const enoughFunds = fundsAvail => value => {
  return value && value > fundsAvail ? value - fundsAvail : undefined;
};

export enum ComparisonOperator {
  GreaterThan = 'GreaterThan',
  LessThan = 'LessThan',
  Equal = 'Equal',
  NotEqual = 'NotEqual',
  GreaterThanOrEqual = 'GreaterThanOrEqual',
  LessThanOrEqual = 'LessThanOrEqual'
}
/**
 * Validates a field in a Redux Form based on a comparison with another field's value.
 *
 * This function is used for field-level validation in Redux Form. It compares the value of a form
 * field with the value of another field (identified by the `key`) and returns a validation message
 * if the comparison fails. The comparison is done using the specified `operator`.
 *
 * **Field-Level Validation in Redux Form:**
 * This function is designed to be used within Redux Form for field-level validation. For more information
 * about field-level validation in Redux Form, please refer to the official documentation:
 * [Redux Form Field-Level Validation](https://redux-form.com/6.6.3/examples/fieldlevelvalidation/).
 *
 * **Comparison Operators:**
 * The `ComparisonOperator` enum defines the following operators:
 * - `GreaterThan`: Checks if the field value is greater than the specified key value.
 * - `LessThan`: Checks if the field value is less than the specified key value.
 * - `Equal`: Checks if the field value is equal to the specified key value.
 * - `NotEqual`: Checks if the field value is not equal to the specified key value.
 * - `GreaterThanOrEqual`: Checks if the field value is greater than or equal to the specified key value.
 * - `LessThanOrEqual`: Checks if the field value is less than or equal to the specified key value.
 *
 * @param {string} key - The key (field name) in the `allValues` object that the current field's value will be compared against.
 * @param {string} message - The validation message to return if the comparison fails.
 * @param {ComparisonOperator} [operator=ComparisonOperator.GreaterThan] - The comparison operator to use. Defaults to `GreaterThan`.
 *
 * @returns {string | undefined} - Returns the validation message if the comparison fails, otherwise `undefined`.
 *
 * @example
 * // Example usage: Validate that a field value is greater than the value of another field.
 * const result = validateFieldWithKey('minValue', 'Value must be greater than minValue')(10, { minValue: 5 });
 * console.log(result);  // undefined (since 10 > 5)
 *
 * const result2 = validateFieldWithKey('minValue', 'Value must be greater than minValue')(3, { minValue: 5 });
 * console.log(result2);  // 'Value must be greater than minValue' (since 3 < 5)
 *
 * @example
 * // Example usage with different operators.
 * const result3 = validateFieldWithKey('maxValue', 'Value must be less than maxValue', ComparisonOperator.LessThan)(2, { maxValue: 5 });
 * console.log(result3);  // undefined (since 2 < 5)
 *
 * const result4 = validateFieldWithKey('maxValue', 'Value must be less than maxValue', ComparisonOperator.LessThan)(6, { maxValue: 5 });
 * console.log(result4);  // 'Value must be less than maxValue' (since 6 > 5)
 */

export const validateFieldWithKey =
  (key: string, message: string, operator: ComparisonOperator = ComparisonOperator.GreaterThan) =>
  (value: any, allValues: Record<string, any>): string | undefined => {
    const keyValue = _toNumber(_get(allValues, key, 0));
    const valueToCompare = _toNumber(value);
    if (keyValue === 0 || valueToCompare === 0) {
      return undefined;
    }
    switch (operator) {
      case ComparisonOperator.GreaterThan:
        return valueToCompare > keyValue ? message : undefined;
      case ComparisonOperator.LessThan:
        return valueToCompare < keyValue ? message : undefined;
      case ComparisonOperator.Equal:
        return valueToCompare === keyValue ? message : undefined;
      case ComparisonOperator.NotEqual:
        return valueToCompare !== keyValue ? message : undefined;
      case ComparisonOperator.GreaterThanOrEqual:
        return valueToCompare >= keyValue ? message : undefined;
      case ComparisonOperator.LessThanOrEqual:
        return valueToCompare <= keyValue ? message : undefined;
      default:
        return undefined;
    }
  };

/**
 * TODO. Move the logic from this function into a yup validation schemas for reward form steps
 */
export const validate = values => {
  const errors: { [k: string]: string } = {};

  if (values.account_lifetime_max_available && values.account_interval_max_available) {
    if (
      Number(values.account_interval_max_available) > Number(values.account_lifetime_max_available)
    ) {
      errors.account_interval_max_available = t(`${TGlobalListPage}.NotGreaterThanUserLimit`);
    }
  }

  if (values.campaign_interval_max_available && values.campaign_lifetime_max_available) {
    if (
      Number(values.campaign_interval_max_available) >
      Number(values.campaign_lifetime_max_available)
    ) {
      errors.campaign_interval_max_available = t(`${TGlobalListPage}.NotGreaterThanRewardLimit`);
    }
  }
  if (values.campaign_interval_max_available) {
    if (Number(values.campaign_interval_max_available) < 0) {
      errors.campaign_interval_max_available = t(`${TGlobalListPage}.ValueMustBePositive`);
    }
  }

  if (values.campaign_lifetime_max_available) {
    if (Number(values.campaign_lifetime_max_available) < 0) {
      errors.campaign_lifetime_max_available = t(`${TGlobalListPage}.ValueMustBePositive`);
    }
  }

  if (values.campaign_interval_units) {
    if (Number(values.campaign_interval_units) < 0) {
      errors.campaign_interval_units = t(`${TGlobalListPage}.ValueMustBePositive`);
    }
  }

  if (values.account_lifetime_max_available) {
    if (Number(values.account_lifetime_max_available) < 0) {
      errors.account_lifetime_max_available = t(`${TGlobalListPage}.ValueMustBePositive`);
    }
  }

  if (values.account_interval_max_available) {
    if (Number(values.account_interval_max_available) < 0) {
      errors.account_interval_max_available = t(`${TGlobalListPage}.ValueMustBePositive`);
    }
  }

  if (values.account_interval_units) {
    if (Number(values.account_interval_units) < 0) {
      errors.account_interval_units = t(`${TGlobalListPage}.ValueMustBePositive`);
    }
  }

  const validateNumber = (values, field, errors, condition, key, params?) => {
    if (values[field] && condition) {
      errors[field] = t(`${TGlobalListPage}.${key}`, params);
    }
  };

  if (values.file) {
    const { filename } = values.file;
    if (!filename.match(/\.(csv|xlsx)$/)) {
      errors.file = t(`${TGlobalListPage}.InvalidCsvXlsx`);
    }
  }

  validateNumber(
    values,
    'campaign_lifetime_max_available',
    errors,
    values['campaign_lifetime_max_available'] < 0,
    'ValueMustBePositive'
  );

  validateNumber(
    values,
    'campaign_interval_max_available',
    errors,
    values['campaign_interval_max_available'] < 0,
    'ValueMustBePositive'
  );

  validateNumber(
    values,
    'campaign_interval_units',
    errors,
    values['campaign_interval_units'] <= 0,
    'ValueMustBeMoreThanZero'
  );

  validateNumber(
    values,
    'account_lifetime_max_available',
    errors,
    values['account_lifetime_max_available'] < 0,
    'ValueMustBePositive'
  );

  validateNumber(
    values,
    'account_interval_max_available',
    errors,
    values['account_interval_max_available'] < 0,
    'ValueMustBePositive'
  );

  validateNumber(
    values,
    'account_interval_units',
    errors,
    values['account_interval_units'] <= 0,
    'ValueMustBeMoreThanZero'
  );

  return errors;
};

export const isValueEmpty = value => {
  return _isNil(value) || value === '';
};

export const isObjNilOrEmpty = (obj: object) => {
  return _isNil(obj) || Object.keys(obj).length === 0;
};

export const validateImageFile = file => {
  const hasImageExtension =
    file.type === 'image/gif' ||
    file.type === 'image/jpeg' ||
    file.type === 'image/png' ||
    file.type === 'image/jpg' ||
    file.type === 'image/svg+xml';

  if (!hasImageExtension) {
    message.error('You can only upload JPG/JPEG/PNG/SVG file!');
  }

  return hasImageExtension;
};

/**
 * Allows to use yup-schema validation with redux-form
 */
export const validateFromSchema =
  <T extends ObjectShape>(schema: ObjectSchema<T>) =>
  formValues => {
    const formErrors = {};

    try {
      schema.validateSync(formValues, { abortEarly: false });
    } catch (errors) {
      errors.inner.forEach(err => {
        _set(formErrors, err.path, err.message);
      });
    }

    return formErrors;
  };
